'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { loadCss, loadJs } from '@nx-frontend/features/web-editor/util';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { generalEnv } from '@nx-frontend/shared/env';
import { useRouter, useParams, useSearchParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $user } from '@nx-frontend/shared/store/user';
import { $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import { PopupType } from '@nx-frontend/shared/types';
import { copyToClipboard } from '@nx-frontend/shared/utils';
import { NextRouterType } from '@nx-frontend/shared/utils';
import { getOpenInvitePopup } from '@nx-frontend/widgets/popups';

interface ChatContext {
  channelId?: string;
  messageTimestamp?: number;
}

const customElementSelector = 'nim-workspace-chat';

export const isChatBundleLoaded = () => {
  if (typeof document === 'object') {
    return document.createElement(customElementSelector).constructor !== HTMLElement;
  }
  return false;
};

const routerType = NextRouterType.APP;

export function ChatWithWebComponent() {
  const componentRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const [app, currentWorkspace, user, role, orgInfo, billingInfo, canManageMembers] = useUnit([
    $app,
    $currentWorkspace,
    $user,
    $orgRoleState,
    $orgInfo,
    $billingInfo,
    $wpCanManageMembers,
  ]);
  const [openModal] = useUnit([openModalEvent]);
  const workspaceId = currentWorkspace.globalId;
  const orgId = currentWorkspace.orgId;
  const userId = user.id;
  const sessionId = app.sessionId;
  const userRole = role.role;

  const { toast } = useToast();
  const router = useRouter();
  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  const searchParams = useSearchParams();
  const params = useParams();
  const slug = useMemo(() => (params?.slug as string[]) || [], [params]);
  const [, , channelId, messageTimestamp] = slug;
  const channelIdFromQuery = searchParams?.get('channelId') || '';
  const messageTimestampFromQuery = searchParams?.get('messageTimestamp') || '';

  // in order to avoid chat component recreation during url changes withing this page, the url props must be taken into account only on initial page loading, hence using refs here
  const channelIdRef = useRef<string | undefined>(channelId);
  const messageTimestampRef = useRef<string | undefined>(messageTimestamp);
  const channelIdFromQueryRef = useRef(Array.isArray(channelIdFromQuery) ? channelIdFromQuery[0] : channelIdFromQuery);
  const messageTimestampFromQueryRef = useRef(
    Array.isArray(messageTimestampFromQuery) ? messageTimestampFromQuery[0] : messageTimestampFromQuery
  );

  /**
   * Callback for when chat is loaded
   */
  const onChatLoaded = useCallback(() => {
    setIsLoaded(true);

    if (document?.querySelectorAll(customElementSelector).length === 0) {
      const nimChat = document.createElement(customElementSelector);

      let chatContext: ChatContext = {};
      if (channelIdRef.current) {
        chatContext = {
          ...chatContext,
          channelId: channelIdRef.current,
        };
      }
      if (channelIdFromQueryRef.current != null && channelIdFromQueryRef.current) {
        chatContext = {
          ...chatContext,
          channelId: channelIdFromQueryRef.current,
        };
      }

      if (messageTimestampRef.current) {
        chatContext = {
          ...chatContext,
          messageTimestamp: +messageTimestampRef.current,
        };
      }
      if (messageTimestampFromQueryRef.current != null && messageTimestampFromQueryRef.current) {
        chatContext = {
          ...chatContext,
          messageTimestamp: +messageTimestampFromQueryRef.current,
        };
      }

      const noMessagesText: string | undefined =
        userId === 1 ? 'You will be able to leave a message as soon as you log into the account.' : undefined;

      Object.assign(nimChat, {
        apiGatewayToken: sessionId,
        apiGatewayUrl: generalEnv.gatewayUrl,
        chatApiUrl: generalEnv.chatApiUrl,
        chatWsUrl: generalEnv.chatWsUrl,
        staticAssetUrlPrefix: generalEnv.staticAssetPrefix,
        context: {
          orgId,
          userId,
          userRole: userRole,
          workspaceId,
        },
        chatContext,
        theme: 'cyan-cyan-light',
        nimLanguage: 'en',
        nimUserPilotActions: {
          videoChatClick: '1652708840zFde3075',
        },
        nimChannelNoMessagesText: noMessagesText,
        nimDmNoMessagesText: noMessagesText,
        nimSingleChannelNoMessagesText: noMessagesText,
      });

      nimChat.addEventListener('nimCopy', (e: Event) => {
        const event = e as CustomEvent<ChatContext>;
        const { channelId, messageTimestamp } = event.detail;
        copyToClipboard(`${window.location.origin}/space/${workspaceId}/chat/${channelId}/${messageTimestamp}`);
        toast({ title: 'Copied' });
      });

      nimChat.addEventListener('nimChangeChannel', (e: Event) => {
        const event = e as CustomEvent<string>;
        routerRef.current.push(`/space/${workspaceId}/chat/${event.detail}`);
      });

      nimChat.addEventListener('nimAddAll', () => {
        getOpenInvitePopup({ role, orgInfo, billingInfo, currentWorkspace, canManageMembers }, openModal, {
          routerType,
          routerPushFn: routerRef.current.push,
        })({
          subjectType: 'workspace',
          payloadType: PopupType.INVITE,
          globalId: workspaceId,
          title: currentWorkspace.title,
        });
      });

      componentRef.current?.appendChild(nimChat);
    }
  }, [
    workspaceId,
    orgId,
    userId,
    sessionId,
    userRole,
    orgInfo,
    billingInfo,
    canManageMembers,
    role,
    currentWorkspace,
    toast,
    setIsLoaded,
    openModal,
  ]);

  /**
   * Load tasks bundle js and css
   */
  useEffect(() => {
    loadJs({
      id: 'chat-bundle-script',
      url: generalEnv.chatBundleJsUrl,
    });

    loadCss({
      id: 'tasks-bundle-css',
      url: generalEnv.tasksBundleCssUrl,
    });

    let IID: NodeJS.Timer | number | null = null;
    if (!isChatBundleLoaded()) {
      IID = setInterval(() => {
        if (isChatBundleLoaded()) {
          onChatLoaded();
          IID && clearInterval(IID);
        }
      }, 500);
    } else {
      onChatLoaded();
    }

    return () => {
      IID && clearInterval(IID);
      IID && document?.querySelector(customElementSelector)?.remove();
    };
  }, [onChatLoaded]);

  return (
    <div data-channelId={channelId} className="h-full w-full" ref={componentRef}>
      {!isLoaded && (
        <div className="h-full w-full flex justify-center items-center flex-col">
          <Spinner />
        </div>
      )}
    </div>
  );
}
